import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Divider } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomTheme, resetCustomTheme } from '../../features/theme/themeSlice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TableContainerCardBox } from '../../components/commonComponent/StyledComponents';
import TableHeadingTypography from '../../components/commonComponent/TableHeadingTypography';
import Button from '../../components/Button/Button';
import useRequestLock from '../../hooks/useRequestLock';
import BadgeLabelHeading from '../../components/commonComponent/BadgeLabelHeading';
import { ThemeinsertAndUpdate, GetThemeDetails } from '../../api/Theme/ThemeColors';
import { useAuth } from '../../hooks/useAuth';
import { useSnackbar } from '../../components/MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import ColorLensIcon from '@mui/icons-material/ColorLens';

const OptionBox = styled(Paper)(({ boxcolor }) => ({
  '&&': {
    width: '40px !important',
    height: '40px !important',
    boxSizing: 'content-box',
    borderRadius: '12px !important',
    borderWidth: '2px !important',
    borderStyle: 'solid',
    borderColor: boxcolor,
    padding: '5px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'visible',
    boxShadow: 'none',
  }
}));

const ColorPickerBox = styled(Paper)(({ boxcolor, isSelected }) => ({
  '&&': {
    width: '40px !important',
    height: '40px !important',
    boxSizing: 'content-box',
    borderRadius: '12px !important',
    borderWidth: '2px !important',
    borderStyle: 'solid',
    borderColor: isSelected ? '#0099ff' : boxcolor,
    padding: '5px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'visible',
    boxShadow: 'none',
    background: 'transparent',
  }
}));

// const InnerColorBox = styled(Box)(({ selectedcolor }) => ({
//   width: '100%',
//   height: '100%',
//   borderRadius: '8px',
//   background: selectedcolor || 'conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   position: 'relative',
//   // border: selectedcolor ? 'none' : '1px dashed rgba(0,0,0,0.2)',
// }));
const InnerColorBox = styled(Box)(({ selectedcolor }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  background:
    selectedcolor ||
    'conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&::after': {
    content: '""',
    width: '40%',
    height: '40%',
    background: '#fff',
    borderRadius: '50%',
    position: 'absolute',
    zIndex: 2,
  }
}));


const IconContainer = styled(Box)(({ hascolor }) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  '& .MuiSvgIcon-root': {
    fontSize: 30,
    color: hascolor ?
      (getContrastColor(hascolor) === '#000000' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)') :
      'white',
    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))',
  }
}));

const getContrastColor = (hexColor) => {
  if (!hexColor) return "#FFFFFF";
  const c = hexColor.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? "#000000" : "#FFFFFF";
};


// const colorOptions = {
//   activeButton: [
//     '#2E37A4',
//     '#00D3C7',
//     '#E04F16',
//     '#0E9384',
//     '#3538CD',
//     '#2F80ED',
//     '#D4166C',
//     '#800080',
//   ],
// };


const colorOptions = {
  activeButton: [
    // "#1875C8", 
    "#00AAB1",
    "#6EAE4D",
    "#FFAE04",
    "#FF5437",
    // "#821EA4", 
    // "#D8E0E2",
  ],
  activeTextColor: [
    "#52526C",
    "#F8F9FA",
    "#1A1A1A",
    "#000000",
  ],
};


const themeOptions = {
  sidebar: [
    {
      type: 'image',
      preview: '/assets/images/themesetting/Group11.png',
      value: '#134686',
      name: 'Deep Sapphire / Dark Blue',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/Group12.png',
      value: '#1d4561ff',
      name: 'Midnight Blue / Dark Slate Blue',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/Group13.png',
      value: '#162891',
      name: 'Royal Blue / Strong Cobalt',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/Group14.png',
      value: '#121b2e',
      name: 'Gunmetal / Charcoal Navy',
    },
  ],
  header: [
    {
      type: 'image',
      preview: '/assets/images/themesetting/whiteheader.png',
      value: '#ffffff',
      name: 'White Header',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/darkheader.png',
      value: 'linear-gradient(90deg, #484545 0%, #030303 100%)',
      name: 'Dark Header',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/blueheader.png',
      // value: 'linear-gradient(to right, #0071f3, #73b4ff)',
      value: 'linear-gradient(180deg, #7A7DF9 0%, #3538CD 100%)',
      name: 'Blue Header',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/greenheader.png',
      value: 'linear-gradient(90deg, #2EF4E9 0%, #01ADA3 100%)',
      name: 'Green Header',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/darkblue.png',
      value: 'linear-gradient(180deg, #4E59DB 0%, #0C1367 100%)',
      name: 'Dark Green Header',
    },
    {
      type: 'image',
      preview: '/assets/images/themesetting/darkorangeheader.png',
      value: 'linear-gradient(90deg, #F46730 0%, #B73704 100%)',
      name: 'Red Header',
    },
  ],
};

const ThemeSettingPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const reduxColors = useSelector(state => state.theme.customColors || {});
  const isDarkMode = theme.palette?.mode === 'dark';
  const [lightModeActiveButton, setLightModeActiveButton] = useState('');
  const [lightModeActiveText, setLightModeActiveText] = useState('');
  const isDarkModeFromStore = useSelector(state => state.theme.isDarkMode);
  const { showSnackbar } = useSnackbar();
  // const [snack, showSnackbar] = useState({
  //   open: false,
  //   message: '',
  //   severity: 'success',
  // });
  const { user } = useAuth();
  const { loading: requestLoading, execute } = useRequestLock();
  const schoolID = user && user.schoolID;
  const [draftColors, setDraftColors] = useState(
    reduxColors && Object.keys(reduxColors).length > 0 ? reduxColors : {}
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [colorPickerColors, setColorPickerColors] = useState({
    sidebar: '',
    header: '',
    activeButton: '',
    activeTextColor: ''
  });

  const DEFAULT_THEME = {
    sidebar: '',
    header: '',
    activeButton: '',
    activeTextColor: '',

  };
  const saveTheme = async () => {
    const payload = {
      schoolID: schoolID,
      modifiedBy: user?.adminID || user?.employeeID || null,
      sidebarBackground: draftColors.sidebar || "",
      headerBackground: draftColors.header || "",
      activeButtonColor: draftColors.activeButton || "",
      activeTextColor: draftColors.activeTextColor || "",
    };

    try {
      const response = await ThemeinsertAndUpdate(payload);

      if (response?.status === 200 || response?.success) {
        showSnackbar('Theme updated successfully', 'success');

        dispatch(setCustomTheme(draftColors));
        localStorage.setItem(`originalTheme_${schoolID}`, JSON.stringify(draftColors));
        setHasChanges(false);
      } else {
        throw new Error("Failed to update theme");
      }
    } catch (err) {
      showSnackbar('Failed to update theme', 'error');
    }
  };

  const handleSaveTheme = () =>
    execute('saveTheme', async () => {
      await saveTheme();
    });

  const isPredefined = (section, color) => {
    if (!color) return false;
    const options = section === 'sidebar' || section === 'header'
      ? themeOptions[section]?.map(opt => opt.value)
      : colorOptions[section];
    return options?.includes(color);
  };

useEffect(() => {
  const loadAndApplyTheme = async () => {
    if (!user?.schoolID) return;

    const originalKey = `originalTheme_${user.schoolID}`;
    const modeKey = `themeMode_${user.schoolID}`;

    let originalTheme = null;

    const cached = localStorage.getItem(originalKey);
    if (cached) {
      try {
        originalTheme = JSON.parse(cached);
      } catch (e) {
        return "";
      }
    }

    if (!originalTheme) {
      try {
        const response = await GetThemeDetails(user.schoolID);
        if (response?.success && response?.data) {
          originalTheme = {
            sidebar: response.data.sidebarBackground || "",
            header: response.data.headerBackground || "",
            activeButton: response.data.activeButtonColor || "",
            activeTextColor: response.data.activeTextColor || "",
          };
          localStorage.setItem(originalKey, JSON.stringify(originalTheme));
        }
      } catch (err) {
        originalTheme = { sidebar: "", header: "", activeButton: "", activeTextColor: "" };
      }
    }

    const savedMode = localStorage.getItem(modeKey) || 'light';
    const effectiveTheme = { ...originalTheme };

    if (savedMode === 'dark' || isDarkMode) {
      effectiveTheme.sidebar = "";
      effectiveTheme.header = "";
      effectiveTheme.activeButton = "";
      effectiveTheme.activeTextColor= "";
    }

    dispatch(setCustomTheme(effectiveTheme));
    setDraftColors(effectiveTheme);

    setColorPickerColors({
      sidebar: isPredefined('sidebar', effectiveTheme.sidebar) ? '' : effectiveTheme.sidebar,
      header: isPredefined('header', effectiveTheme.header) ? '' : effectiveTheme.header,
      activeButton: isPredefined('activeButton', effectiveTheme.activeButton) ? '' : effectiveTheme.activeButton,
      activeTextColor: isPredefined('activeTextColor', effectiveTheme.activeTextColor) ? '' : effectiveTheme.activeTextColor,
    });

    setHasChanges(false);
  };

  loadAndApplyTheme();
}, [user?.schoolID, dispatch, isDarkMode]);

  const handleChange = (section, color, source = 'predefined') => {
    setDraftColors(prev => ({ ...prev, [section]: color }));
    setHasChanges(true);
    if (source === 'predefined') {
      setColorPickerColors(prev => ({ ...prev, [section]: '' }));
    } else {
      setColorPickerColors(prev => ({ ...prev, [section]: color }));
    }
  };

  // const handleUpdate = () => {
  //   const mergedColors = { ...(reduxColors || {}), ...(draftColors || {}) };
  //   dispatch(setCustomTheme(mergedColors));
  //   setHasChanges(false);

  // };

  const resetTheme = async () => {
    if (!schoolID) return;

    const payload = {
      schoolID: schoolID,
      modifiedBy: user?.adminID || user?.employeeID || null,
      sidebarBackground: DEFAULT_THEME.sidebar,
      headerBackground: DEFAULT_THEME.header,
      activeButtonColor: DEFAULT_THEME.activeButton,
      activeTextColor: DEFAULT_THEME.activeTextColor,
    };

    try {
      const response = await ThemeinsertAndUpdate(payload);
      if (response?.status === 200 || response?.success) {
        dispatch(setCustomTheme(DEFAULT_THEME));
        setDraftColors(DEFAULT_THEME);
        localStorage.setItem(`originalTheme_${schoolID}`, JSON.stringify(DEFAULT_THEME));
        setColorPickerColors({
          sidebar: '',
          header: '',
          activeButton: '',
          activeTextColor: ''
        });
        setHasChanges(false);
        showSnackbar('Default Theme Applied', 'success');
      } else {
        throw new Error('Reset API failed');
      }
    } catch (err) {
      return;
    }
  };

  const handleReset = () =>
    execute('resetTheme', async () => {
      await resetTheme();
    });

  useEffect(() => {
  if (!user?.schoolID) return;

  const originalKey = `originalTheme_${user.schoolID}`;
  const modeKey = `themeMode_${user.schoolID}`;

  const cached = localStorage.getItem(originalKey);
  if (!cached) return;

  try {
    const originalTheme = JSON.parse(cached);
    const effectiveTheme = { ...originalTheme };

    localStorage.setItem(modeKey, isDarkMode ? 'dark' : 'light');

    if (isDarkMode) {
      effectiveTheme.sidebar = "";
      effectiveTheme.header = "";
      effectiveTheme.activeButton = "";
      effectiveTheme.activeTextColor = "";
    }

    dispatch(setCustomTheme(effectiveTheme));
    setDraftColors(effectiveTheme);
  } catch (e) {
   return "";
  }
}, [isDarkMode, user?.schoolID, dispatch]);

  const getSectionOptions = section => {
    const options = themeOptions[section] || colorOptions[section] || [];
    if (!isDarkMode) return options;

    const disallowed = new Set(['#e17055', '#dc3545', '#e5252a']);
    return options.filter(c => {
      const value = typeof c === 'string' ? c : c.value;
      return !disallowed.has(String(value).toLowerCase());
    });
  };


  const renderOptions = section =>
    getSectionOptions(section).map(option => {
      const isObj = typeof option === 'object' && option !== null;
      const value = isObj ? option.value : option;
      const preview = isObj && option.preview ? option.preview : null;
      const isImage = Boolean(preview);
      const isSectionDisabled = isDarkMode && (section === 'sidebar' || section === 'header' || section === 'activeButton' || section === 'activeTextColor');
      const isSelected = draftColors?.[section] === value;
      const activeColor = '#0099ff';

      const neutralBorder = theme.palette.custom?.borderColor || theme.palette.divider;
      const isActiveButtonSection = section === 'activeButton' || section === 'activeTextColor';

      return (
        <Grid key={isImage ? preview : value}>
          <OptionBox
            boxcolor={isImage
              ? (isSelected ? activeColor : neutralBorder)
              : (isActiveButtonSection
                ? (isSelected ? activeColor : neutralBorder)
                : value)}
            onClick={() => {
              if (!isSectionDisabled) {
                handleChange(section, value, 'predefined');
              }
            }}
            sx={{
              ...(isSectionDisabled ? { pointerEvents: 'none', opacity: 0.6 } : {}),
              ...((isImage || isActiveButtonSection) ? { width: '40px', height: '40px', } : {}),
              ...(isSelected ? {
                borderColor: `${activeColor} !important`,
                borderWidth: '2px !important',
                borderStyle: 'solid',
              } : {}),
            }}
          >
            {isImage ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 0,
                  backgroundImage: `url(${preview})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            ) : (
              <Box sx={{ width: '100%', height: '100%', background: value, borderRadius: '8px' }} />
            )}
            {isSelected && !isSectionDisabled && (
              <CheckCircleIcon
                sx={{
                  position: 'absolute',
                  top: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 16,
                  color: activeColor,
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  padding: '2px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  zIndex: 3,
                  pointerEvents: 'none',
                }}
              />
            )}
          </OptionBox>
        </Grid>
      );
    });

  const renderColorPicker = (section, currentColor) => {
    const isSectionDisabled = isDarkMode && (section === 'sidebar' || section === 'header' || section === 'activeButton' || section === 'activeTextColor');
    const neutralBorder = theme.palette.custom?.borderColor || theme.palette.divider;
    const activeColor = '#0099ff';
    const pickerColor = colorPickerColors[section];
    const isSelected = Boolean(pickerColor && pickerColor.trim() !== "");

    return (
      <Grid item>
        <Box sx={{ position: 'relative', padding: '5px' }}>
          <ColorPickerBox
            boxcolor={neutralBorder}
            isselected={isSelected}
            onClick={() => {
              if (!isSectionDisabled) {
                document.getElementById(`color-input-${section}`).click();
              }
            }}

            sx={{
              ...(isSectionDisabled ? { pointerEvents: 'none', opacity: 0.6 } : {}),
              '&:hover': {
                borderColor: activeColor,
                // transform: 'scale(1.05)',
              },
              // transition: 'all 0.2s ease',
              ...(isSelected ? {
                borderColor: `${activeColor} !important`,
                borderWidth: '2px !important',
                borderStyle: 'solid',
              } : {}),
            }}
          >
            <InnerColorBox selectedcolor={colorPickerColors[section] || (draftColors[section] && !isPredefined(section, draftColors[section]) ? draftColors[section] : null)}>{/* Use pickerColor instead of currentColor */}
              <IconContainer hascolor={pickerColor}> {/* Use pickerColor instead of currentColor */}

              </IconContainer>
            </InnerColorBox>
            {isSelected && !isSectionDisabled && (
              <CheckCircleIcon
                sx={{
                  position: 'absolute',
                  top: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 16,
                  color: activeColor,
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  padding: '2px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  zIndex: 3,
                  pointerEvents: 'none',
                }}
              />
            )}
          </ColorPickerBox>

          {/* Hidden color input */}
          <input
            id={`color-input-${section}`}
            type="color"
            value={colorPickerColors[section] || draftColors[section] || '#000000'}
            disabled={isSectionDisabled}
            onChange={(e) => handleChange(section, e.target.value, 'picker')}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              cursor: isSectionDisabled ? 'not-allowed' : 'pointer',
              pointerEvents: isSectionDisabled ? 'none' : 'auto',
              width: '100%',
              height: '100%',
              padding: 0,
              border: 'none',
              background: 'transparent',
            }}
          />
        </Box>
      </Grid>
    );
  };

  // const buttonColor = reduxColors?.activeButton || theme.palette.background?.bgpurple || '#8f01ff';

  return (
    <TableContainerCardBox>
      <Box py={3} textAlign="center" px={{ xs: 2, sm: 3 }}>
        <TableHeadingTypography fontWeight={700} text="Theme Setting" variant="h6" />
        <Box mt={1}>
          <Divider textAlign="center">
            <span className="fs-14">Update Theme Styling Setting</span>
          </Divider>
        </Box>
      </Box>
      <Box px={{ xs: 2, sm: 3 }} py={2}>
        {/* Sidebar Section */}
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'transparent',
            borderRadius: 2,
            border: `1px solid ${theme.palette.custom?.borderColor || theme.palette.divider}`,
            p: 3,
            pt: 4,
            mb: 3,
          }}
        >
          <BadgeLabelHeading
            text="Sidebar Background"
            mb={0}
            sx={{
              position: 'absolute',
              top: 0,
              left: 16,
              transform: 'translateY(-50%)',
              '& span': { background: 'linear-gradient(135deg, #2196f3, #21cbf3) !important' },
            }}
          />
          <Grid container spacing={2} alignItems="center">
            {renderOptions('sidebar')}
            {renderColorPicker('sidebar', draftColors.sidebar)}
          </Grid>
          {isDarkMode && (
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Sidebar color selection is disabled in dark mode.
            </Typography>
          )}
        </Box>

        {/* DONT REMOVE THIS COMMENTED CODE */}
        {/* Header Section */}
        {/* <Box
          sx={{
            position: 'relative',
            bgcolor: 'transparent',
            borderRadius: 2,
            border: `1px solid ${theme.palette.custom?.borderColor || theme.palette.divider}`,
            p: 3,
            pt: 4,
            mb: 3,
          }}
        >
          <BadgeLabelHeading
            text="Header Background"
            mb={0}
            sx={{
              position: 'absolute',
              top: 0,
              left: 16,
              transform: 'translateY(-50%)',
              '& span': { background: 'linear-gradient(135deg, #2196f3, #21cbf3) !important' },
            }}
          />
          <Grid container spacing={2}>
            {renderOptions('header')}
          </Grid>
          {isDarkMode && (
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Header color selection is disabled in dark mode.
            </Typography>
          )}
        </Box> */}

        {/* Active Button Section */}
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'transparent',
            borderRadius: 2,
            border: `1px solid ${theme.palette.custom?.borderColor || theme.palette.divider}`,
            p: 3,
            pt: 4,
            mb: 3,
          }}
        >
          <BadgeLabelHeading
            text="Active Button"
            mb={0}
            sx={{
              position: 'absolute',
              top: 0,
              left: 16,
              transform: 'translateY(-50%)',
              '& span': { background: 'linear-gradient(135deg, #2196f3, #21cbf3) !important' },
            }}
          />
          <Grid container spacing={2} alignItems="center">
            {renderOptions('activeButton')}
            {renderColorPicker('activeButton', draftColors.activeButton)}
          </Grid>
          {isDarkMode && (
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Active button color selection is disabled in dark mode.
            </Typography>
          )}
        </Box>

        {/* Active Button Text Color Section */}
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'transparent',
            borderRadius: 2,
            border: `1px solid ${theme.palette.custom?.borderColor || theme.palette.divider}`,
            p: 3,
            pt: 4,
            mb: 3,
          }}
        >
          <BadgeLabelHeading
            text="Active Button Text Color"
            mb={0}
            sx={{
              position: 'absolute',
              top: 0,
              left: 16,
              transform: 'translateY(-50%)',
              '& span': { background: 'linear-gradient(135deg, #2196f3, #21cbf3) !important' },
            }}
          />
          <Grid container spacing={2} alignItems="center">
            {renderOptions('activeTextColor')}
            {renderColorPicker('activeTextColor', draftColors.activeTextColor)}
          </Grid>
          {isDarkMode && (
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Active button text color selection is disabled in dark mode.
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ pt: 0, px: 0, pb: 3, display: 'flex', gap: 1 }}>
          <Button
            sx={{
              minWidth: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', opacity: isDarkMode ? 0.6 : 1,
              pointerEvents: isDarkMode ? 'none' : 'auto',
            }}
            bgColor='#e7e7e8'
            textColor={'#1f2937'}
            padding="8px 10px"
            fontSize="12px"
            onClick={handleReset}
            disabled={isDarkMode || requestLoading.resetTheme}
          >
            <i class="fa-sharp fa-regular fa-arrows-rotate" style={{ fontSize: "10px" }}></i> Reset Theme
          </Button>
          <Button
            sx={{ minWidth: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', opacity: isDarkMode ? 0.6 : 1, }}
            bgColor={theme.palette.background.bgpurple}
            textColor={theme.palette.background.textwhite}
            padding="10px 10px"
            fontSize="12px"
            onClick={handleSaveTheme}
            disabled={isDarkMode || !hasChanges || requestLoading.saveTheme}
          >
            <i class="fa-sharp fa-regular fa-check" style={{ fontSize: "10px" }}></i>  Update Theme
          </Button>
        </Box>
      </Box>
    </TableContainerCardBox>
  );
};

export default ThemeSettingPage;
